const MAX_ERRORS = 8;
const COLLAPSE_FIRST_CHECK_MS = 5000;
const COLLAPSE_RECHECK_MS = 10000;
const COLLAPSE_STRIKES = 2;

const errors = [];
let errorCount = 0;
let overlay = null;

function supports(property, value) {
    try {
        return window.CSS && CSS.supports(property, value) ? 'yes' : 'NO';
    } catch {
        return 'NO';
    }
}

function probeUnitPx(expression) {
    const probe = document.createElement('div');
    probe.style.cssText = `position:absolute;visibility:hidden;width:${expression}`;
    document.body.appendChild(probe);
    const width = probe.getBoundingClientRect().width;
    probe.remove();
    return `${Math.round(width * 100) / 100}px`;
}

function featureReport() {
    const app = document.getElementById('app');
    return [
        ['user agent', navigator.userAgent],
        ['viewport', `${window.innerWidth}x${window.innerHeight} @${window.devicePixelRatio}x`],
        ['#app size', app ? `${Math.round(app.getBoundingClientRect().width)}x${Math.round(app.getBoundingClientRect().height)}` : 'MISSING'],
        ['--cqw x 100', probeUnitPx('calc(var(--cqw) * 100)')],
        ['--cqh x 100', probeUnitPx('calc(var(--cqh) * 100)')],
        ['css min()', supports('width', 'min(1vw, 1vh)')],
        ['css custom prop in calc', supports('width', 'calc(var(--x) * 1)')],
        ['container queries', supports('width', '1cqw')],
        ['AbortSignal.timeout', typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? 'yes' : 'NO'],
        ['crypto.randomUUID', typeof crypto !== 'undefined' && crypto.randomUUID ? 'yes' : 'NO'],
        ['secure context', window.isSecureContext ? 'yes' : 'NO'],
        ['online', navigator.onLine ? 'yes' : 'NO'],
        ['service worker', 'serviceWorker' in navigator ? 'yes' : 'NO']
    ];
}

function escapeHtml(value) {
    return String(value).replace(/[&<>]/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;'}[c]));
}

function render() {
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.style.cssText = [
            'position:fixed', 'top:0', 'left:0', 'right:0', 'bottom:0',
            'z-index:2147483647', 'background:rgba(0,0,0,0.94)', 'color:#fff',
            'font:16px/1.5 monospace', 'padding:24px', 'overflow:auto',
            'white-space:pre-wrap', 'word-break:break-word'
        ].join(';');
        overlay.addEventListener('click', dismissDiagnostics);
        window.addEventListener('keydown', dismissDiagnostics);
        document.body.appendChild(overlay);
    }

    const report = featureReport()
        .map(([label, value]) => `  ${label.padEnd(24)} ${escapeHtml(value)}`)
        .join('\n');

    const list = errors.length
        ? errors.map((e, i) => `<span style="color:#F44336">${i + 1}. ${escapeHtml(e)}</span>`).join('\n\n')
        : '  (none)';
    const omitted = errorCount > errors.length ? `\n\n  ...and ${errorCount - errors.length} more` : '';

    overlay.innerHTML =
        `<strong style="font-size:22px">Buzztime TV — diagnostics</strong>` +
        `<span style="opacity:0.6"> (click or press any key to close)</span>\n\n` +
        `<strong>Errors (${errorCount})</strong>\n${list}${omitted}\n\n<strong>Environment</strong>\n${report}`;
}

// Buffer an error without surfacing it. A working app must never be covered by a
// debug overlay, so background failures only show up once the report is opened.
export function recordError(message) {
    try {
        errorCount++;
        if (errors.length < MAX_ERRORS) errors.push(message);
        if (overlay) render();
    } catch {
        // never let the diagnostic surface become the failure
    }
}

export function showDiagnostics(message) {
    try {
        if (message) {
            errorCount++;
            if (errors.length < MAX_ERRORS) errors.push(message);
        }
        render();
    } catch {
        // never let the diagnostic surface become the failure
    }
}

function dismissDiagnostics() {
    if (!overlay) return;
    window.removeEventListener('keydown', dismissDiagnostics);
    overlay.remove();
    overlay = null;
}

function describe(error) {
    if (!error) return 'Unknown error';
    if (error instanceof Error) return `${error.name}: ${error.message}\n${error.stack || ''}`;
    return String(error);
}

export function installDiagnostics(app) {
    app.config.errorHandler = (error, _instance, info) => {
        console.error('[Vue]', info, error);
        recordError(`Vue (${info})\n${describe(error)}`);
    };

    window.addEventListener('error', e => {
        recordError(e.error ? describe(e.error) : `${e.message} @ ${e.filename}:${e.lineno}`);
    });

    window.addEventListener('unhandledrejection', e => {
        recordError(`Unhandled rejection\n${describe(e.reason)}`);
    });

    window.__bgnDiag = () => showDiagnostics();
    window.__bgnDiagClose = () => dismissDiagnostics();

    watchForCollapse();
}

// The only automatic trigger: the app is rendering nothing at all. Requires two
// consecutive readings so a mount or transition can't trip it, and keeps polling
// so a collapse that happens later (e.g. on app launch) is still caught.
function watchForCollapse() {
    let strikes = 0;

    const check = () => {
        if (!overlay) {
            const root = document.getElementById('app');
            strikes = (!root || root.getBoundingClientRect().height < 1) ? strikes + 1 : 0;

            if (strikes >= COLLAPSE_STRIKES) {
                showDiagnostics('App rendered with no visible content (#app has zero height).');
                return;
            }
        }
        setTimeout(check, COLLAPSE_RECHECK_MS);
    };

    setTimeout(check, COLLAPSE_FIRST_CHECK_MS);
}
